import { Box, TextField, Button, Collapse } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { focusTokens } from '../../../styles/theme/focus';

/**
 * Capa superior de listados (toolbar de filtros + campos).
 * Un solo lugar para propagar cambios a todos los menús que importen aquí.
 */
export const ListPageFilterContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 0),
  background: theme.palette.background.default,
  borderRadius: theme.spacing(2),
  boxShadow: 'none',
  border: 'none',
}));

export const ListPageFilterRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'nowrap',
  paddingLeft: 0,
  paddingRight: 0,
  gap: theme.spacing(2),
  width: '100%',
  alignItems: 'center',
}));

/** Grid secundario: catálogo de productos (columnas asimétricas). */
export const ListPageCatalogSecondaryFilterRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  paddingLeft: 0,
  paddingRight: 0,
  gridTemplateColumns: '1fr 1fr 1fr 0.8fr 1.2fr',
  gap: theme.spacing(2.5),
  width: '100%',
  alignItems: 'end',
  marginTop: theme.spacing(3),
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: '1fr 1fr 1fr',
  },
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'repeat(5, 1fr)',
  },
}));

/** Grid secundario: registro de inventario (5 columnas iguales). */
export const ListPageEqualSecondaryFilterRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  paddingLeft: 0,
  paddingRight: 0,
  gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
  gap: theme.spacing(2.5),
  width: '100%',
  alignItems: 'end',
  marginTop: theme.spacing(3),
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: 'repeat(3, 1fr)',
  },
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'repeat(5, 1fr)',
  },
}));

/** Grid secundario: cuatro columnas iguales (p. ej. gestión de ventas). */
export const ListPageQuadSecondaryFilterRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  paddingLeft: 0,
  paddingRight: 0,
  gridTemplateColumns: '1fr 1fr 1fr 1fr',
  gap: theme.spacing(2.5),
  width: '100%',
  alignItems: 'end',
  marginTop: theme.spacing(3),
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: 'repeat(2, 1fr)',
  },
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'repeat(4, 1fr)',
  },
}));

/** Grid secundario: tres columnas iguales (p. ej. CRM gestión de clientes). */
export const ListPageTripleSecondaryFilterRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  paddingLeft: 0,
  paddingRight: 0,
  gridTemplateColumns: '1fr 1fr 1fr',
  gap: theme.spacing(2.5),
  width: '100%',
  alignItems: 'end',
  marginTop: theme.spacing(3),
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: 'repeat(2, 1fr)',
  },
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'repeat(3, 1fr)',
  },
}));

export const ListPageFilterTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1.5),
    backdropFilter: 'blur(10px)',
    transition: 'all 0.2s ease-in-out',
    ...(theme.palette.mode === 'dark'
      ? {
          backgroundColor: alpha(theme.palette.common.white, 0.06),
          '&:hover': {
            backgroundColor: alpha(theme.palette.common.white, 0.1),
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
          },
          '&.Mui-focused': {
            backgroundColor: alpha(theme.palette.common.white, 0.12),
            transform: 'translateY(-1px)',
            boxShadow: `0 4px 20px ${alpha(theme.palette.success.main, 0.35)}`,
          },
        }
      : {
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          },
          '&.Mui-focused': {
            backgroundColor: '#ffffff',
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 20px rgba(12, 187, 154, 0.15)',
          },
        }),
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    color: theme.palette.text.secondary,
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.75)',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: focusTokens.idleBorderColor,
    '& legend': {
      maxWidth: 'none',
      width: 'fit-content',
      minWidth: 0,
      padding: '0 8px',
      boxSizing: 'content-box',
    },
  },
  '& .MuiOutlinedInput-root:hover:not(.Mui-error):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline':
    {
      borderColor: focusTokens.hoverBorderColor,
      borderWidth: 1,
    },
  '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
    borderColor: focusTokens.borderColor,
    borderWidth: 2,
  },
}));

export const ListPageFilterModernButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1, 2.5),
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
  },
}));

export const ListPageFilterToolbarOutlinedButton = styled(ListPageFilterModernButton)(
  ({ theme }) => ({
    minWidth: 120,
    borderColor: theme.palette.divider,
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: 'transparent',
      boxShadow: theme.shadows[1],
    },
  }),
);

export const ListPageFilterToolbarSearchButton = styled(ListPageFilterModernButton)(
  ({ theme }) => ({
    minWidth: 120,
    background: 'linear-gradient(to right, #06b6d4, #10b981)',
    color: 'white',
    '&:hover': {
      background: 'linear-gradient(to right, #0891b2, #059669)',
    },
  }),
);

export const ListPageFilterSearchContainer = styled(Box)({
  flex: 1,
  minWidth: 200,
});

export const ListPageFilterCollapse = styled(Collapse)({
  '&.MuiCollapse-entered': {
    overflow: 'visible',
  },
});
